import type { LabelState } from "./states";

/*
 * The label basket (ADR 0018 › Label basket): one per garage, the labels
 * waiting to be printed.
 */

/** A label in the basket: a part's (with its location path, top level first) or a blank one. */
export interface BasketEntry {
  code: string;
  part: { name: string; locationPath: readonly string[] } | null;
}

/** Names compare as people expect: "Shelf 2" before "Shelf 10", case and accents aside. */
const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

function comparePaths(a: readonly string[], b: readonly string[]): number {
  for (let i = 0; i < Math.min(a.length, b.length); i++) {
    const order = collator.compare(a[i] ?? "", b[i] ?? "");
    if (order !== 0) return order;
  }
  // A location before the ones inside it.
  return a.length - b.length;
}

/**
 * Print order, so the strip or sheet follows a walk through the garage: part labels by location
 * path, then part name; parts without a location after them; blank labels last.
 */
export function sortBasket<T extends BasketEntry>(entries: readonly T[]): T[] {
  const rank = (entry: BasketEntry) =>
    !entry.part ? 2 : entry.part.locationPath.length === 0 ? 1 : 0;
  return [...entries].sort((a, b) => {
    const order = rank(a) - rank(b);
    if (order !== 0) return order;
    if (a.part && b.part) {
      const byPlace = comparePaths(a.part.locationPath, b.part.locationPath);
      if (byPlace !== 0) return byPlace;
      const byName = collator.compare(a.part.name, b.part.name);
      if (byName !== 0) return byName;
    }
    return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
  });
}

/**
 * The label a part gets printed again when it goes into the basket: its newest attached or pending
 * one; none when it has neither, so it gets a new code.
 */
export function labelToReprint<T extends { state: LabelState; createdAt: string }>(
  labels: readonly T[],
): T | null {
  let newest: T | null = null;
  for (const label of labels) {
    if (label.state !== "attached" && label.state !== "pending") continue;
    if (!newest || label.createdAt > newest.createdAt) newest = label;
  }
  return newest;
}

/**
 * What taking a label out of the basket does: one never printed is deleted, since no sticker of it
 * exists; a printed one leaves the basket and keeps its state.
 */
export function basketRemoval(label: { lastPrintedAt: string | null }): "delete" | "keep" {
  return label.lastPrintedAt === null ? "delete" : "keep";
}
